import { useEffect, useState } from "react"
import Image from "next/image"
import useEmblaCarousel from "embla-carousel-react"
import { useReducedMotion } from "framer-motion"
import {
  ArrowLeft01Icon,
  ArrowRight01Icon,
} from "@hugeicons-pro/core-stroke-rounded"
import { HugeiconsIcon } from "@hugeicons/react"
import { Button } from "@seamapi/ui/components/ui/button"
import styles from "./AuthQuoteCarousel.module.css"

// Published customer testimonials. Keep excerpts and attribution tied to their sources.
const testimonials = [
  {
    quote:
      "Seam takes what would otherwise be an insurmountable headache and turns it into an almost trivial task for our engineers to knock out.",
    name: "Pierre-Camille Hamana",
    role: "CEO, Hospitable",
    logo: "/auth/customer-logos/hospitable.svg",
    source: "https://www.seam.co/",
  },
  {
    quote:
      "A simple API that we could integrate and have access to all these devices in a matter of seconds.",
    name: "François Gouelo",
    role: "CEO, Enso Connect",
    logo: "/auth/customer-logos/enso-connect.svg",
    source: "https://www.seam.co/customers",
  },
  {
    quote:
      "This is the best integration platform I've ever seen across a wide variety of products, so very cool.",
    name: "Rexford Weber",
    role: "Cofounder & Head of Engineering, Guestwise",
    logo: "/auth/customer-logos/guestwise.svg",
    source: "https://www.seam.co/solutions/short-term-rental",
  },
] as const

/** Embla (the shadcn carousel primitive) with the Console's shared buttons. */
export function AuthQuoteCarousel() {
  const reducedMotion = useReducedMotion() ?? true
  const [viewport, carousel] = useEmblaCarousel({
    loop: true,
    duration: reducedMotion ? 0 : 25,
  })
  const [current, setCurrent] = useState(0)

  useEffect(() => {
    if (!carousel) return
    const syncSelection = () => setCurrent(carousel.selectedScrollSnap())
    syncSelection()
    carousel.on("select", syncSelection).on("reInit", syncSelection)
    return () => {
      carousel.off("select", syncSelection).off("reInit", syncSelection)
    }
  }, [carousel])

  return (
    <section
      className={styles.carousel}
      aria-label="Customer testimonials"
      aria-roledescription="carousel"
      onKeyDown={(event) => {
        if (event.target instanceof HTMLAnchorElement) return
        if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
          event.preventDefault()
          if (event.key === "ArrowLeft") carousel?.scrollPrev()
          else carousel?.scrollNext()
        }
      }}
    >
      <div ref={viewport} className={styles.viewport} aria-live="polite">
        <div className={styles.track}>
          {testimonials.map((testimonial, index) => (
            <figure
              key={testimonial.name}
              className={styles.slide}
              role="group"
              aria-roledescription="slide"
              aria-label={`${index + 1} of ${testimonials.length}`}
              aria-hidden={current !== index}
              inert={current !== index}
            >
              <blockquote cite={testimonial.source}>
                “{testimonial.quote}”
              </blockquote>
              <figcaption>
                <a href={testimonial.source} target="_blank" rel="noreferrer">
                  <Image
                    src={testimonial.logo}
                    width={64}
                    height={64}
                    alt=""
                    className={styles.logo}
                  />
                  <span className={styles.attribution}>
                    <span>{testimonial.name}</span>
                    <span className={styles.role}>{testimonial.role}</span>
                  </span>
                </a>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
      <div className={styles.controls}>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className={styles.control}
          aria-label="Previous testimonial"
          onClick={() => carousel?.scrollPrev()}
          disabled={!carousel}
        >
          <HugeiconsIcon icon={ArrowLeft01Icon} size={16} aria-hidden="true" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className={styles.control}
          aria-label="Next testimonial"
          onClick={() => carousel?.scrollNext()}
          disabled={!carousel}
        >
          <HugeiconsIcon icon={ArrowRight01Icon} size={16} aria-hidden="true" />
        </Button>
      </div>
    </section>
  )
}
